import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Trash2, Pencil, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { formatApiError, API_BASE } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { COMPLIANCE_COLORS } from "@/lib/format";
import { useSearchParams } from "react-router-dom";

const COMPLIANCE_TYPES = ["GST", "ITR", "TDS", "ROC", "Audit", "PF", "ESI", "Custom"];
const ENTITY_TYPES = ["Individual", "Proprietorship", "Partnership", "LLP", "Pvt Ltd", "Public Ltd", "HUF", "Trust"];

const EMPTY = {
    name: "",
    entity_type: "Individual",
    email: "",
    phone: "",
    gstin: "",
    pan: "",
    address: "",
    compliance_types: [],
    assigned_employee_ids: [],
    notes: "",
};

export default function Clients() {
    const { user } = useAuth();
    const [params] = useSearchParams();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState(params.get("q") || "");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [employees, setEmployees] = useState([]);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importing, setImporting] = useState(false);

    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    const load = async () => {
        const { data } = await api.get(`/clients`, { params: { q: q || undefined } });
        setItems(data.items);
        setTotal(data.total);
    };

    useEffect(() => {
        load();
        if (isAdmin) api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };
    const openEdit = (c) => {
        setEditing(c);
        setForm({ ...EMPTY, ...c });
        setOpen(true);
    };

    const save = async () => {
        try {
            const payload = { ...form };
            ["email", "phone", "gstin", "pan", "address", "notes"].forEach((k) => {
                if (payload[k] === "" || payload[k] === undefined) delete payload[k];
            });
            if (editing) await api.patch(`/clients/${editing.id}`, payload);
            else await api.post("/clients", payload);
            toast.success(editing ? "Client updated" : "Client added");
            setOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this client? Related tasks stay untouched.")) return;
        try {
            await api.delete(`/clients/${id}`);
            toast.success("Client deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleComp = (t) =>
        setForm((f) => ({
            ...f,
            compliance_types: f.compliance_types.includes(t)
                ? f.compliance_types.filter((x) => x !== t)
                : [...f.compliance_types, t],
        }));

    const toggleEmp = (id) =>
        setForm((f) => ({
            ...f,
            assigned_employee_ids: f.assigned_employee_ids.includes(id)
                ? f.assigned_employee_ids.filter((x) => x !== id)
                : [...f.assigned_employee_ids, id],
        }));

    const doImport = async () => {
        if (!importFile) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            const { data } = await api.post("/clients/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(data);
            toast.success(`Imported ${data.imported} client${data.imported === 1 ? "" : "s"}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = async () => {
        const token = localStorage.getItem("ca_access_token");
        const r = await fetch(`${API_BASE}/clients/import/template`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "clients_template.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6" data-testid="clients-page">
            <PageHeader
                title="Clients"
                subtitle={`${total} client${total === 1 ? "" : "s"} in your firm`}
                actions={
                    isAdmin && (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setImportFile(null);
                                    setImportResult(null);
                                    setImportOpen(true);
                                }}
                                data-testid="import-csv-btn"
                            >
                                <Upload size={15} className="mr-1.5" /> Import CSV
                            </Button>
                            <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="new-client-btn">
                                <Plus size={16} className="mr-1.5" /> Add client
                            </Button>
                        </>
                    )
                }
            />

            <Card className="p-3 flex items-center gap-2">
                <Search size={16} className="text-slate-400 ml-2" />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, email, GSTIN, PAN…"
                    className="border-0 focus-visible:ring-0"
                    data-testid="client-search"
                />
            </Card>

            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Client</th>
                                <th className="text-left px-4 py-3 font-semibold">Type</th>
                                <th className="text-left px-4 py-3 font-semibold">Compliance</th>
                                <th className="text-left px-4 py-3 font-semibold">GSTIN / PAN</th>
                                <th className="text-left px-4 py-3 font-semibold">Contact</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr
                                    key={c.id}
                                    className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors"
                                    data-testid={`client-row-${c.id}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-800">{c.name}</div>
                                        {c.email && (
                                            <div className="text-xs text-slate-500">{c.email}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{c.entity_type}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {(c.compliance_types || []).map((t) => (
                                                <span
                                                    key={t}
                                                    className={`badge-status ${COMPLIANCE_COLORS[t] || ""}`}
                                                >
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600 font-mono">
                                        {c.gstin || "—"}
                                        <br />
                                        {c.pan || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                                    <td className="px-4 py-3">
                                        {isAdmin && (
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                                                    <Pencil size={14} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => remove(c.id)}
                                                    className="text-rose-600 hover:bg-rose-50"
                                                    data-testid={`delete-client-${c.id}`}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center py-12 text-slate-500">
                                        No clients yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {editing ? "Edit client" : "Add client"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Name">
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="cf-name"
                            />
                        </Field>
                        <Field label="Entity type">
                            <Select
                                value={form.entity_type}
                                onValueChange={(v) => setForm({ ...form, entity_type: v })}
                            >
                                <SelectTrigger data-testid="cf-entity">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ENTITY_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Email">
                            <Input
                                type="email"
                                value={form.email || ""}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                            />
                        </Field>
                        <Field label="Phone">
                            <Input
                                value={form.phone || ""}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            />
                        </Field>
                        <Field label="GSTIN">
                            <Input
                                value={form.gstin || ""}
                                onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                            />
                        </Field>
                        <Field label="PAN">
                            <Input
                                value={form.pan || ""}
                                onChange={(e) => setForm({ ...form, pan: e.target.value })}
                            />
                        </Field>
                        <div className="col-span-2">
                            <Field label="Address">
                                <Textarea
                                    rows={2}
                                    value={form.address || ""}
                                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                                />
                            </Field>
                        </div>
                        <div className="col-span-2">
                            <div className="field-label mb-2">Compliance types</div>
                            <div className="flex flex-wrap gap-2">
                                {COMPLIANCE_TYPES.map((t) => {
                                    const on = form.compliance_types.includes(t);
                                    return (
                                        <button
                                            type="button"
                                            key={t}
                                            onClick={() => toggleComp(t)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                                                on
                                                    ? "bg-primary text-white border-primary"
                                                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                            }`}
                                        >
                                            {t}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {employees.length > 0 && (
                            <div className="col-span-2">
                                <div className="field-label mb-2">Assigned employees</div>
                                <div className="grid grid-cols-2 gap-2">
                                    {employees.map((e) => (
                                        <label
                                            key={e.id}
                                            className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                                        >
                                            <Checkbox
                                                checked={form.assigned_employee_ids.includes(e.id)}
                                                onCheckedChange={() => toggleEmp(e.id)}
                                            />
                                            {e.name} <span className="text-xs text-slate-400">{e.role}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-client">
                            {editing ? "Save changes" : "Create client"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CSV import dialog */}
            <Dialog open={importOpen} onOpenChange={setImportOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-display">Import clients from CSV</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-slate-500">
                            Columns (case-insensitive): <span className="font-mono text-xs">name, entity_type, email, phone, gstin, pan, address, compliance_types (semicolon or comma-separated), notes</span>.
                            Only <b>name</b> is required.
                        </p>
                        <div>
                            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template">
                                <Download size={13} className="mr-1.5" /> Download template
                            </Button>
                        </div>
                        <div
                            className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
                                importFile ? "border-primary bg-blue-50" : "border-slate-300 hover:border-primary"
                            }`}
                            onClick={() => document.getElementById("csv-input")?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.preventDefault();
                                const f = e.dataTransfer.files?.[0];
                                if (f) setImportFile(f);
                            }}
                            data-testid="csv-dropzone"
                        >
                            <Upload size={20} className="mx-auto text-primary" />
                            <div className="text-sm mt-2 font-medium text-slate-700">
                                {importFile ? importFile.name : "Drag & drop or click to select a .csv"}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">Max 2 MB</div>
                            <input
                                id="csv-input"
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                            />
                        </div>
                        {importResult && (
                            <div className="border rounded-md p-3 bg-slate-50 text-sm space-y-1">
                                <div>
                                    Imported <b className="text-emerald-600">{importResult.imported}</b>,
                                    skipped <b className="text-amber-600">{importResult.skipped}</b>.
                                </div>
                                {importResult.errors?.length > 0 && (
                                    <details>
                                        <summary className="text-xs text-slate-500 cursor-pointer">
                                            {importResult.errors.length} row error(s)
                                        </summary>
                                        <ul className="mt-1 text-xs text-rose-600 space-y-0.5">
                                            {importResult.errors.map((e, i) => (
                                                <li key={i}>Line {e.line}: {e.error}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
                        <Button
                            onClick={doImport}
                            disabled={!importFile || importing}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="do-import"
                        >
                            {importing ? "Importing…" : "Import"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
